import { useMemo, useCallback } from 'react'

import { FormattedNumber } from 'react-intl'

import type { ButtonClickedData } from '@b-stock/fe-core/bstock-react'
import {
  AnalyticsEntityType,
  AnalyticsSource,
} from '@b-stock/fe-core/bstock-react'

import AppliedFilterSection from '@/components/listings/search/SearchFilters/shared/AppliedFilterSection'
import PercentageRangeFilter from '@/components/listings/search/SearchFilters/shared/NumericRangeFilter/PercentageRangeFilter'
import type {
  ListingFilter,
  FilterProps,
  AppliedProps,
} from '@/components/listings/search/SearchFilters/types'

// API uses 0-1 range (0.5 = 50%), with "null" meaning "don't filter",
// UI uses 0-100 range, with "100%" treated as "don't filter"
const convertApiToUi = (apiValue: number | null): number => {
  return apiValue !== null ? Math.round(apiValue * 10000) / 100 : 100
}

const convertUiToApi = (uiValue: number): number | null => {
  return uiValue === 100 ? null : Math.round(uiValue * 100) / 10000
}

const MsrpPercentageFilter: ListingFilter<number | null> = ({
  onChange,
  desktopScreen,
  value,
  onError,
}: FilterProps<number | null>) => {
  // Convert API value (0-1) to UI value (0-100) for display
  const uiValue = convertApiToUi(value)

  // Convert UI value (0-100) to API value (0-1) when updating
  const handleChange = (newUiValue: number) => {
    const apiValue = convertUiToApi(newUiValue)
    onChange(apiValue)
  }

  return (
    <PercentageRangeFilter
      selected={uiValue}
      onChange={handleChange}
      onError={onError}
      desktopScreen={desktopScreen}
      analyticsTitle="MSRP Percentage"
    />
  )
}

MsrpPercentageFilter.expandedOnInit = false
MsrpPercentageFilter.isAlwaysDisplayed = true
MsrpPercentageFilter.getOwnProps = ({ state, updateFilters }) => ({
  filterKey: 'msrpPercentage',
  onChange: (msrpPercentage: number | null) => {
    updateFilters({ msrpPercentage })
  },
  value: state.filters.msrpPercentage,
})

MsrpPercentageFilter.label = 'Filters.msrpPercentage'

MsrpPercentageFilter.tooltip = 'Filters.msrpPercentage.tooltip'

MsrpPercentageFilter.getOnExpand = (
  trackButtonClicked: (data: ButtonClickedData) => void
) => {
  return () => {
    trackButtonClicked({
      buttonName: 'filters',
      filter_title: '% of MSRP',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
    })
  }
}

// Custom Applied component that converts API value (0-1) to display percentage (0-100)
const MsrpPercentageApplied = ({
  filtersState,
  updateFilters,
  noClose,
}: AppliedProps) => {
  const apiPercentage = filtersState.msrpPercentage

  const value = useMemo(() => {
    if (apiPercentage === null) {
      return null
    }

    return {
      key: 'msrpPercentage',
      name: (
        <FormattedNumber
          style="percent"
          value={apiPercentage}
          maximumFractionDigits={2}
        />
      ),
    }
  }, [apiPercentage])

  const onRemove = useCallback(() => {
    updateFilters({ msrpPercentage: null })
  }, [updateFilters])

  if (!value) {
    return <></>
  }

  return (
    <AppliedFilterSection
      label={MsrpPercentageFilter.label}
      values={[value]}
      onRemove={onRemove}
      noClose={noClose}
    />
  )
}

MsrpPercentageApplied.displayName = 'MsrpPercentageApplied'

MsrpPercentageFilter.Applied = MsrpPercentageApplied

export default MsrpPercentageFilter
